// montaggio.ts — la scaletta della clip: funzione PURA (niente DOM, niente orologi), provata con node:test.
//
// Dato l'elenco degli eventi del round (sull'orologio del registratore) e le opzioni, decide quali tratti della
// registrazione entrano nella clip, a che velocità e con quali sovrimpressioni. Ordine fisso (SPECIFICA-CLIP §1):
//
//   conto alla rovescia → [tratto accelerato] → ultimi secondi a velocità normale → risata al rallentatore
//   → timbri «HA RISO!»/«HA VINTO!» (fermo immagine) → finale col simbolo e il link
//
// I tempi `daMs`/`aMs` sono tempi della SORGENTE (orologio di `adesso`); `durataMs` e `inizioClipMs` sono tempi
// della CLIP. `velocita` = sorgente/clip: 1 normale, > 1 accelerato, < 1 rallentato, 0 fermo immagine.
// La durata totale non supera mai `secondiMassimi`.
import { ErroreClip } from "./errori.ts";
import type { EventoClip } from "./tipi.ts";

export type TipoSegmento = "conto" | "accelerato" | "normale" | "rallentatore" | "timbro" | "finale";
export type Sovrimpressione = "conto" | "via" | "accelerato" | "rallentatore" | "timbro" | "finale" | "marchio";

export interface Segmento {
  tipo: TipoSegmento;
  daMs: number;
  aMs: number;
  velocita: number;
  sovrimpressioni: Sovrimpressione[];
  /** Durata nella clip. */
  durataMs: number;
  /** Inizio nella clip. */
  inizioClipMs: number;
}

export interface Montaggio {
  segmenti: Segmento[];
  durataMs: number;
  /** Tempo sorgente del VIA (per il conto alla rovescia «3, 2, 1»). */
  viaMs: number;
  /** Tempo sorgente della risata decisa dal server. */
  risataMs: number;
  /** Chi ha riso: la sua metà riceve «HA RISO!», l'altra «HA VINTO!». */
  perdente: "sopra" | "sotto";
  /** Tratti di sorgente che servono davvero (per sapere quali fotogrammi tenere). */
  intervalliUsati: { daMs: number; aMs: number; denso: boolean }[];
}

export interface OpzioniMontaggio {
  secondiMassimi: number;
  secondiPrimaRisata: number;
  rallentatore: number;
  secondiRallentatore: number;
  secondiFinale: number;
  /** v040: il tratto accelerato prima dei secondi normali (false = si taglia: video più fluido, dialoghi chiari) */
  accelerato?: boolean;
}

/** Durate fisse (ms nella clip). */
export const CONTO_MASSIMO_MS = 3000;
export const VIA_MS = 700; // «VIA!» resta sullo schermo all'inizio del tratto dopo il conto
export const TIMBRO_MS = 1500;
export const ACCELERATO_MASSIMO_MS = 3000; // «breve tratto accelerato»
export const ACCELERATO_MINIMO_MS = 600; // sotto questa durata il tratto accelerato non si vede: si salta
export const VELOCITA_MINIMA_ACCELERATO = 1.5; // sotto questo rapporto conviene la velocità normale
/** Quota della finestra del rallentatore che sta PRIMA del momento della risata (il server decide con ritardo). */
export const QUOTA_PRIMA_RISATA = 0.6;

interface Intervallo {
  da: number;
  a: number;
}

/** Tratti attivi (senza pause) tra `da` e `a`. */
function attivi(da: number, a: number, pause: Intervallo[]): Intervallo[] {
  const out: Intervallo[] = [];
  let cursore = da;
  for (const p of pause) {
    if (p.a <= cursore || p.da >= a) continue;
    if (p.da > cursore) out.push({ da: cursore, a: Math.min(p.da, a) });
    cursore = Math.max(cursore, p.a);
    if (cursore >= a) break;
  }
  if (cursore < a) out.push({ da: cursore, a });
  return out.filter((i) => i.a - i.da > 0);
}

/** Gli ultimi `quanto` ms di tempo attivo che finiscono a `fino`, a partire non prima di `da`. */
function ultimi(tratti: Intervallo[], quanto: number): Intervallo[] {
  const out: Intervallo[] = [];
  let resto = quanto;
  for (let i = tratti.length - 1; i >= 0 && resto > 0; i--) {
    const t = tratti[i]!;
    const lung = t.a - t.da;
    if (lung <= resto) {
      out.unshift({ ...t });
      resto -= lung;
    } else {
      out.unshift({ da: t.a - resto, a: t.a });
      resto = 0;
    }
  }
  return out;
}

const somma = (tratti: Intervallo[]) => tratti.reduce((s, t) => s + (t.a - t.da), 0);

/**
 * Costruisce la scaletta.
 * @param inizioMs momento di `avvia()` (inizio del conto alla rovescia)
 * @param fineRegistrazioneMs momento di `ferma()` (ultimo fotogramma disponibile)
 * @throws ErroreClip("nessuna_risata") se il round è finito pari o annullato (perdente null) o senza risata
 */
export function montaggio(eventi: readonly EventoClip[], opzioni: OpzioniMontaggio, inizioMs: number, fineRegistrazioneMs: number): Montaggio {
  const ev = [...eventi].sort((a, b) => a.t - b.t);
  const fine = [...ev].reverse().find((e) => e.tipo === "fine");
  const risata = [...ev].reverse().find((e): e is Extract<EventoClip, { tipo: "risata" }> => e.tipo === "risata");
  if (fine && fine.tipo === "fine" && fine.perdente === null)
    throw new ErroreClip("nessuna_risata", "Il round è finito pari o è stato annullato: niente clip.");
  if (!risata && !fine) throw new ErroreClip("nessuna_risata", "Nessuna risata registrata in questo round: niente clip.");
  const perdente = fine && fine.tipo === "fine" && fine.perdente ? fine.perdente : risata!.chi;
  const viaEv = ev.find((e) => e.tipo === "via");
  const fineReg = Math.max(inizioMs, fineRegistrazioneMs);
  const via = Math.min(Math.max(viaEv ? viaEv.t : inizioMs, inizioMs), fineReg);
  let tr = risata ? risata.t : fine!.t;
  tr = Math.min(Math.max(tr, via), fineReg);

  // Pause: coppie pausa→ripresa. Una pausa ancora aperta si chiude alla ripresa successiva, alla risata (il
  // server ha visto la risata, quindi la camera c'era) o alla fine della registrazione.
  const pause: Intervallo[] = [];
  let aperta: number | null = null;
  for (const e of ev) {
    if (e.tipo === "pausa" && aperta === null) aperta = e.t;
    else if (e.tipo === "ripresa" && aperta !== null) {
      pause.push({ da: aperta, a: e.t });
      aperta = null;
    }
  }
  if (aperta !== null) pause.push({ da: aperta, a: aperta < tr ? tr : fineReg });
  // Una pausa che contiene la risata finisce alla risata.
  for (const p of pause) if (p.da < tr && p.a > tr) p.a = tr;

  const M = opzioni.secondiMassimi * 1000;
  const vel = Math.min(1, Math.max(0.05, opzioni.rallentatore));
  let S = opzioni.secondiRallentatore * 1000; // durata del rallentatore nella clip
  let P = opzioni.secondiPrimaRisata * 1000;
  let T = TIMBRO_MS;
  let F = opzioni.secondiFinale * 1000;
  let C = Math.min(CONTO_MASSIMO_MS, Math.max(0, via - inizioMs));

  // Riduzioni se le parti fisse non stanno nella durata massima: prima i secondi normali, poi il conto, poi il
  // rallentatore, poi il timbro, infine il finale (mai sotto 1 s).
  const riduci = () => {
    let eccesso = C + P + S + T + F - M;
    const taglia = (v: number, min: number) => {
      const t = Math.min(eccesso, Math.max(0, v - min));
      eccesso -= t;
      return v - t;
    };
    if (eccesso > 0) P = taglia(P, 0);
    if (eccesso > 0) C = taglia(C, 0);
    if (eccesso > 0) S = taglia(S, 1000);
    if (eccesso > 0) T = taglia(T, 500);
    if (eccesso > 0) F = taglia(F, 1000);
    if (eccesso > 0) S = taglia(S, 0);
    if (eccesso > 0) T = taglia(T, 0);
    if (eccesso > 0) F = taglia(F, 0);
  };
  riduci();

  // Finestra del rallentatore in tempo sorgente, dentro il tratto attivo che contiene la risata.
  const attiviTutti = attivi(via, fineReg, pause);
  const tratto = attiviTutti.find((i) => i.da <= tr && tr <= i.a) ?? { da: tr, a: tr };
  let L = S * vel;
  let s0 = Math.max(tratto.da, tr - L * QUOTA_PRIMA_RISATA);
  let s1 = Math.min(tratto.a, s0 + L);
  if (s1 - s0 < L) s0 = Math.max(tratto.da, s1 - L); // se dopo la risata c'è poco, si allarga indietro
  L = s1 - s0;
  const Sclip = L > 0 ? L / vel : 0;

  // Ultimi P ms attivi prima del rallentatore.
  const primaRall = attivi(via, s0, pause);
  const normali = ultimi(primaRall, P);
  const inizioNormale = normali[0]?.da ?? s0;
  // Il resto (dal VIA all'inizio dei secondi normali) va accelerato, se c'è e se avanza tempo.
  const resto = attivi(via, inizioNormale, pause);
  const E = somma(resto);
  const fissi = C + somma(normali) + Sclip + T + F;
  const libero = Math.max(0, M - fissi);
  let accelerati: Intervallo[] = [];
  let velAcc = 1;
  let durAcc = 0;
  if (E > 0) {
    if (E <= libero && E / Math.min(libero, ACCELERATO_MASSIMO_MS) < VELOCITA_MINIMA_ACCELERATO) {
      // Poco da recuperare: entra a velocità normale (partita corta).
      accelerati = resto;
      velAcc = 1;
      durAcc = E;
    } else if (opzioni.accelerato !== false && Math.min(libero, ACCELERATO_MASSIMO_MS) >= ACCELERATO_MINIMO_MS) {
      durAcc = Math.min(libero, ACCELERATO_MASSIMO_MS);
      velAcc = E / durAcc;
      accelerati = resto;
    } // altrimenti il tratto si salta (c'è solo il taglio)
  }

  const segmenti: Segmento[] = [];
  let orologio = 0;
  const aggiungi = (tipo: TipoSegmento, daMs: number, aMs: number, velocita: number, durataMs: number, sovr: Sovrimpressione[]) => {
    if (durataMs <= 0) return;
    segmenti.push({ tipo, daMs, aMs, velocita, durataMs, inizioClipMs: orologio, sovrimpressioni: sovr });
    orologio += durataMs;
  };
  if (C > 0) aggiungi("conto", via - C, via, 1, C, ["conto", "marchio"]);
  const viaClip = orologio;
  for (const t of accelerati)
    aggiungi(velAcc > 1 ? "accelerato" : "normale", t.da, t.a, velAcc, (t.a - t.da) / velAcc, velAcc > 1 ? ["accelerato", "marchio"] : ["marchio"]);
  for (const t of normali) aggiungi("normale", t.da, t.a, 1, t.a - t.da, ["marchio"]);
  if (Sclip > 0) aggiungi("rallentatore", s0, s1, vel, Sclip, ["rallentatore", "marchio"]);
  aggiungi("timbro", s1, s1, 0, T, ["timbro", "marchio"]);
  aggiungi("finale", s1, s1, 0, F, ["finale"]);
  // «VIA!» sui segmenti che cadono nei primi VIA_MS dopo il conto.
  if (C > 0)
    for (const s of segmenti)
      if (s.tipo !== "conto" && s.inizioClipMs < viaClip + VIA_MS && s.tipo !== "finale" && s.tipo !== "timbro") s.sovrimpressioni.unshift("via");

  const intervalliUsati = segmenti
    .filter((s) => s.tipo !== "finale")
    .map((s) => ({ daMs: s.daMs, aMs: s.aMs, denso: s.velocita <= 1 }));
  return { segmenti, durataMs: orologio, viaMs: via, risataMs: tr, perdente, intervalliUsati };
}

/** Il punto della sorgente da mostrare al tempo `tClipMs` della clip, con il suo segmento. */
export function puntoSorgente(m: Montaggio, tClipMs: number): { segmento: Segmento; sorgenteMs: number; dentroMs: number } | null {
  for (const s of m.segmenti) {
    if (tClipMs >= s.inizioClipMs && tClipMs < s.inizioClipMs + s.durataMs) {
      const dentro = tClipMs - s.inizioClipMs;
      return { segmento: s, sorgenteMs: s.velocita === 0 ? s.daMs : Math.min(s.aMs, s.daMs + dentro * s.velocita), dentroMs: dentro };
    }
  }
  return null;
}
